import { useCallback, useEffect, useState } from 'react';
import { Alert, Form, message, Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { request } from '../../../../shared/src/apis';
import { addUserRewardsApi, getUserTransactionsAndRewardsApi } from '../../utils/apisConsts';
import { errorMessage } from '../../utils/commonStringsConsts';
import { stepperMoving } from '../../stores/actions';
import helper from '../../utils/helper';
import { ConfirmRewardsModal } from './components/ConfirmRewardsModal';
import { RewardsSection } from './components/RewardsSection';
import { TransactionInfoCard } from './components/TransactionInfoCard';
import { UserInformationCard } from './components/UserInformationCard';
import { IAddRewardsFormValues } from 'src/utils/commonInterfaces';
import { UserStatusTypeID } from '../../utils/userStatusConsts';

const CURRENCY = 'SAR';

const formatAmount = (value?: number | string | null) => {
    if (value === null || value === undefined || value === '') return 'N/A';
    return `${helper.amountFormatter(value)} ${CURRENCY}`;
};

const formatDate = (value?: string | null) => {
    if (!value) return 'N/A';
    return helper.changeDateFormat(value);
};

export const AddReward = () => {
    const [form] = Form.useForm<IAddRewardsFormValues>();
    const [loader, setLoader] = useState(false);
    const [visibleModal, setVisibleModal] = useState(false);
    const [pendingValues, setPendingValues] = useState<IAddRewardsFormValues | null>(null);
    const [userTransactionsAndRewards, setUserTransactionsAndRewards] = useState<any>(null);
    const [loadingRewardsData, setLoadingRewardsData] = useState(false);
    const userInfo = useSelector((state: any) => state?.actionsReducer?.info);
    const dispatch = useDispatch();
    const isBlockedUser = userInfo?.userStatusTypeID === UserStatusTypeID.Blocked;

    const fetchUserTransactionsAndRewards = useCallback(async () => {
        if (!userInfo?.userID || isBlockedUser) {
            setUserTransactionsAndRewards(null);
            return;
        }

        setLoadingRewardsData(true);
        try {
            const resData: any = await dispatch(
                request({
                    method: 'GET',
                    url: getUserTransactionsAndRewardsApi,
                    params: { userID: userInfo.userID },
                })
            );

            const body = resData?.data;

            if (body?.status === 200) {
                setUserTransactionsAndRewards(body?.data ?? null);
                return;
            }

            setUserTransactionsAndRewards(null);
            message.error(body?.message || errorMessage, 5);
        } catch {
            setUserTransactionsAndRewards(null);
        } finally {
            setLoadingRewardsData(false);
        }
    }, [dispatch, userInfo?.userID, isBlockedUser]);

    useEffect(() => {
        fetchUserTransactionsAndRewards();
    }, [fetchUserTransactionsAndRewards]);


    const handleOpenModal = (values: IAddRewardsFormValues) => {
        if (isBlockedUser) return;
        setPendingValues(values);
        setVisibleModal(true);
    };

    const handleCloseModal = () => {
        if (!loader) {
            setVisibleModal(false);
            setPendingValues(null);
        }
    };

    const handleConfirmAddRewards = async () => {
        if (!pendingValues || loader || isBlockedUser) return;

        setLoader(true);
        const payload = {
            userID: userInfo?.userID,
            amount: pendingValues.rewardsAmount,
            sendSMS: pendingValues.sendSms,
        };

        try {
            const apiData: any = await dispatch(
                request({ method: 'POST', url: addUserRewardsApi, data: payload })
            );
            const res = await apiData?.data;

            if (res?.status === 201 || res?.status === 200) {
                message.success(res?.message || 'Rewards added successfully', 5);
                setVisibleModal(false);
                setPendingValues(null);
                dispatch(stepperMoving(0));
                form.resetFields();
                return;
            }

            message.error(res?.message || errorMessage, 5);
        } catch {
            message.error(errorMessage, 5);
        } finally {
            setLoader(false);
        }
    };
    return (
        <div className="py-2 pb-6">
            <UserInformationCard
                userInfo={userInfo}
                salaryDisplay={formatAmount(userInfo?.netSalary)}
                maxBalanceDisplay={formatAmount(userInfo?.maxBalance)}
            />
            {isBlockedUser ? (
                <Alert
                    message="Rewards can not be added to a blocked user."
                    type="error"
                    showIcon
                    className="mb-5"
                />
            ) : (
                <>
                    <Spin spinning={loadingRewardsData}>
                        <TransactionInfoCard
                            hasTransfer={true}
                            lastTransferAmountDisplay={formatAmount(userTransactionsAndRewards?.lastUserTransaction?.requestedAmount)}
                            lastTransferDateDisplay={formatDate(userTransactionsAndRewards?.lastUserTransaction?.dateOfRequest)}
                        />
                        <RewardsSection
                            form={form}
                            rewardBalanceDisplay={formatAmount(userTransactionsAndRewards?.wallet?.totalAmount)}
                            onSubmit={handleOpenModal}
                        />
                    </Spin>
                    <ConfirmRewardsModal
                        visible={visibleModal}
                        loading={loader}
                        pendingValues={pendingValues}
                        userInfo={userInfo}
                        rewardsAmountDisplay={pendingValues ? formatAmount(pendingValues.rewardsAmount) : ''}
                        onConfirm={handleConfirmAddRewards}
                        onClose={handleCloseModal}
                    />
                </>
            )}
        </div>
    );
};
